import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PhotoUpload } from "@/components/PhotoUpload";
import { AvailabilityEditor } from "@/components/dashboard/AvailabilityEditor";
import { BookingsPanel } from "@/components/dashboard/BookingsPanel";
import { ReviewsPanel } from "@/components/dashboard/ReviewsPanel";
import { FeaturedCard } from "@/components/dashboard/FeaturedCard";
import { ListingCard } from "@/components/dashboard/ListingCard";
import { BillingRow } from "@/components/dashboard/BillingRow";
import { CalendarFeedCard } from "@/components/dashboard/CalendarFeedCard";
import { PayoutCard } from "@/components/dashboard/PayoutCard";
import { AudioUpload } from "@/components/AudioUpload";
import { LocationInput } from "@/components/LocationInput";

const statusStyles = {
  pending: "bg-foreground/10 text-foreground border-foreground/30",
  approved: "bg-primary/15 text-primary border-primary/30",
  rejected: "bg-muted text-muted-foreground border-border",
  published: "bg-primary/15 text-primary border-primary/30",
};

export default function PractitionerDashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(undefined);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ business_name: "", bio: "", phone: "", photo: "", business_card: "", city: "", country: "", category_ids: [], subcategory_id: "", services: [] });
  const [learnForm, setLearnForm] = useState({ title: "", topic: "", summary: "", content: "", audio_url: "", related_subcategory: "" });
  const [submissions, setSubmissions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/practitioners/me/profile").then((r) => {
      setProfile(r.data);
      if (r.data) setForm({
        business_name: r.data.business_name, bio: r.data.bio, phone: r.data.phone || "", photo: r.data.photo || "", business_card: r.data.business_card || "",
        city: r.data.city_name || "", country: r.data.country || "",
        category_ids: r.data.category_ids?.length ? r.data.category_ids : (r.data.category_id ? [r.data.category_id] : []),
        subcategory_id: r.data.subcategory_id || "",
        services: r.data.services || [],
      });
    });
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/learn-mine/submissions").then((r) => setSubmissions(r.data));
  }, []);

  const selectedCategories = form.category_ids.map((id) => categories.find((c) => c.id === id)).filter(Boolean);
  const toggleCategory = (id) => setForm((f) => {
    const on = f.category_ids.includes(id);
    if (!on && f.category_ids.length >= 5) { toast.info("You can choose up to 5 categories"); return f; }
    const category_ids = on ? f.category_ids.filter((c) => c !== id) : [...f.category_ids, id];
    const stillValid = category_ids.some((cid) => categories.find((c) => c.id === cid)?.subcategories?.some((s) => s.id === f.subcategory_id));
    return { ...f, category_ids, subcategory_id: stillValid ? f.subcategory_id : "" };
  });

  const saveProfile = async (e, { done = false } = {}) => {
    e?.preventDefault();
    if (form.category_ids.length === 0) { toast.error("Choose at least one category"); return; }
    setBusy(true);
    try {
      const payload = { ...form, category_id: form.category_ids[0], subcategory_id: form.subcategory_id || null, services: form.services.map(({ name, price, duration }) => ({ name, price: Number(price), duration })) };
      const { data } = await api.post("/practitioners/me/profile", payload);
      setProfile(data);
      toast.success(data.status === "pending" ? "Profile submitted — The Team will review it soon." : "Profile updated.");
      if (done) navigate(`/practitioner/${data.id}`);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const submitLearn = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/learn", learnForm);
      setSubmissions([data, ...submissions]);
      setLearnForm({ title: "", topic: "", summary: "", content: "", audio_url: "", related_subcategory: "" });
      toast.success("Submitted for review — thank you for contributing.");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const setService = (i, key, value) => {
    const services = [...form.services];
    services[i] = { ...services[i], [key]: value };
    setForm({ ...form, services });
  };

  if (profile === undefined) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
      <h1 className="font-display text-4xl sm:text-5xl text-foreground mb-2">My Studio</h1>
      <p className="text-sm text-muted-foreground mb-8">Manage your practice on O.R.E.C.</p>

      {profile && (
        <div className={`rounded-lg border px-4 py-3 mb-8 text-sm ${statusStyles[profile.status]}`} data-testid="profile-status-banner">
          {profile.status === "pending" && "Your profile is awaiting the Team's review. You'll go live once approved."}
          {profile.status === "approved" && "Your profile is live in the community."}
          {profile.status === "rejected" && `Your profile wasn't approved this time.${profile.review_note ? ` Note: ${profile.review_note}` : ""} You can edit and it will be re-reviewed.`}
        </div>
      )}

      <ListingCard profile={profile} />
      <FeaturedCard profile={profile} />
      <BillingRow profile={profile} />
      <PayoutCard profile={profile} />
      <CalendarFeedCard profile={profile} />

      <Tabs defaultValue="profile">
        <TabsList className="mb-8 flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="dash-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="availability" data-testid="dash-tab-availability">Availability</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="dash-tab-bookings">Bookings & earnings</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="dash-tab-reviews">Reviews</TabsTrigger>
          <TabsTrigger value="learn" data-testid="dash-tab-learn">Learn Library</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <form onSubmit={saveProfile} className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Business name</Label>
                <Input data-testid="profile-business-name" required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone (optional)</Label>
                <Input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Profile photo or logo</Label>
                <div className="mt-2">
                  <PhotoUpload value={form.photo} onChange={(url) => setForm({ ...form, photo: url })} />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Business card</Label>
                <div className="mt-2">
                  <PhotoUpload value={form.business_card} onChange={(url) => setForm({ ...form, business_card: url })} label="business card" wide testid="business-card-upload" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bio</Label>
              <Textarea data-testid="profile-bio" rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Where you practice</Label>
              <div className="mt-2">
                <LocationInput idPrefix="profile" city={form.city} country={form.country}
                  onChange={({ city, country }) => setForm((f) => ({ ...f, city, country }))} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Categories</Label>
                <span className="text-xs text-muted-foreground" data-testid="profile-category-count">Choose up to 5 · {form.category_ids.length} selected</span>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="profile-category-chips">
                {categories.map((c) => {
                  const on = form.category_ids.includes(c.id);
                  const full = !on && form.category_ids.length >= 5;
                  return (
                    <button key={c.id} type="button" onClick={() => toggleCategory(c.id)} aria-pressed={on} disabled={full}
                      data-testid={`profile-category-chip-${c.id}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"} ${full ? "opacity-40 cursor-not-allowed" : ""}`}>
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Specialty</Label>
                <Select value={form.subcategory_id} onValueChange={(v) => setForm({ ...form, subcategory_id: v })} disabled={selectedCategories.length === 0}>
                  <SelectTrigger data-testid="profile-subcategory-select" className="mt-2"><SelectValue placeholder={selectedCategories.length ? "Specialty" : "Choose a category first"} /></SelectTrigger>
                  <SelectContent>
                    {selectedCategories.map((c) => (
                      <SelectGroup key={c.id}>
                        <SelectLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{c.name}</SelectLabel>
                        {(c.subcategories || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Services & pricing (CAD)</Label>
                <Button type="button" variant="outline" size="sm" data-testid="profile-add-service-btn"
                  onClick={() => setForm({ ...form, services: [...form.services, { name: "", price: "", duration: "" }] })}>
                  <Plus size={14} className="mr-1" /> Add service
                </Button>
              </div>
              <div className="space-y-3">
                {form.services.map((s, i) => (
                  <div key={i} className="grid grid-cols-[1fr_100px_110px_36px] gap-2 items-center">
                    <Input placeholder="Service name" value={s.name} onChange={(e) => setService(i, "name", e.target.value)} />
                    <Input placeholder="Price" type="number" min="0" value={s.price} onChange={(e) => setService(i, "price", e.target.value)} />
                    <Input placeholder="Duration" value={s.duration} onChange={(e) => setService(i, "duration", e.target.value)} />
                    <button type="button" onClick={() => setForm({ ...form, services: form.services.filter((_, j) => j !== i) })}
                      className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button data-testid="profile-save-btn" type="submit" disabled={busy} className="rounded-full px-8">
                {busy ? "Saving…" : profile ? "Save changes" : "Submit for review"}
              </Button>
              <Button data-testid="profile-done-btn" type="button" variant="outline" disabled={busy} className="rounded-full px-8" onClick={(e) => saveProfile(e, { done: true })}>
                <Check size={14} className="mr-1.5" /> Done — view my page
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="availability"><AvailabilityEditor profile={profile} /></TabsContent>
        <TabsContent value="bookings"><BookingsPanel /></TabsContent>
        <TabsContent value="reviews"><ReviewsPanel /></TabsContent>

        <TabsContent value="learn">
          <div className="mb-10">
            <h2 className="font-display text-lg md:text-2xl text-foreground mb-1">Contribute to the Learn Library</h2>
            <p className="text-sm text-muted-foreground mb-6">Share your knowledge with the community. The Team reviews every entry before it goes live.</p>
            <form onSubmit={submitLearn} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="learn-submit-title" required placeholder="Title" value={learnForm.title} onChange={(e) => setLearnForm({ ...learnForm, title: e.target.value })} />
                <Input data-testid="learn-submit-topic" required placeholder="Topic (e.g. Crystals, Modalities)" value={learnForm.topic} onChange={(e) => setLearnForm({ ...learnForm, topic: e.target.value })} />
              </div>
              <Input data-testid="learn-submit-summary" placeholder="One-line summary" value={learnForm.summary} onChange={(e) => setLearnForm({ ...learnForm, summary: e.target.value })} />
              <Textarea data-testid="learn-submit-content" required rows={6} placeholder="Your entry… (separate paragraphs with a blank line)" value={learnForm.content} onChange={(e) => setLearnForm({ ...learnForm, content: e.target.value })} />
              <div>
                <p className="text-xs text-muted-foreground mb-2">Audio (optional) — upload or record</p>
                <AudioUpload value={learnForm.audio_url} onChange={(url) => setLearnForm({ ...learnForm, audio_url: url })} />
              </div>
              <Button data-testid="learn-submit-btn" type="submit" disabled={busy} className="rounded-full px-8">Submit for review</Button>
            </form>
          </div>
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">My submissions</h3>
          <div className="space-y-3" data-testid="learn-my-submissions">
            {submissions.map((s) => (
              <div key={s.id} className="silk-card rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="text-foreground">{s.title}</p>
                  <p className="text-xs text-muted-foreground">{s.topic}</p>
                </div>
                <Badge className={`border font-normal ${statusStyles[s.status] || ""}`} variant="outline">{s.status}</Badge>
              </div>
            ))}
            {submissions.length === 0 && <p className="text-sm text-muted-foreground">No submissions yet.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
